import {
  getSyncQueue,
  getQueueCounts,
  removeSyncQueueEntry,
  incrementRetry,
  markSyncQueueEntryError,
  resetSyncQueueEntry,
  setQueueChangeCallback,
  isFailedEntry,
  isHeldEntry,
  isUnattributableEntry,
  isUserUnattributableEntry,
  getActiveSession,
  type SyncQueueEntry,
  type QueueCounts,
} from './offline-store';
import { apiClient } from '@/api/client';
import { billingMutationRule } from '@efacture/web-billing/lib/billing-mutations';
import { getActiveUserId } from '@/lib/active-tenant';
import type { TranslationKey } from '@/lib/i18n';
import { isDesktopApp } from '@/lib/desktop';

const MAX_RETRIES = 5;

/** API error code (middleware/idempotency.ts): an attempt with this key is still running. */
const IDEMPOTENCY_IN_PROGRESS = 'IDEMPOTENCY_IN_PROGRESS';
const STOCK_CONCURRENT = 'STOCK_CONCURRENT';

/** The token left the entry's dossier during the pass (another tab switched it). */
const TENANT_MISMATCH = 'TENANT_MISMATCH';

/** Failure message of a queued never-queued write (a fiscal act, a print, a read sent as POST). */
const FISCAL_NOT_REPLAYED = 'Opération fiscale non rejouée : refaites-la en ligne.';

let isSyncing = false;
const listeners = new Set<(counts: QueueCounts) => void>();

export function onSyncQueueChange(listener: (counts: QueueCounts) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyListeners(counts: QueueCounts) {
  listeners.forEach((fn) => fn(counts));
}

// Bridge: offline-store notifies us on any queue change (add/remove/error),
// so listeners (OfflineContext) get updates even from addToSyncQueue.
setQueueChangeCallback(notifyListeners);

export async function processSyncQueue(): Promise<{
  processed: number;
  failed: number;
  held: number;
}> {
  if (isSyncing) return { processed: 0, failed: 0, held: 0 };
  isSyncing = true;

  let processed = 0;
  let failed = 0;
  let held = 0;

  // Read ONCE per pass: replaying half a queue under one tenant and half
  // under another (a refresh landing mid-pass) is exactly the failure
  // mode being closed.
  const active = getActiveSession();

  try {
    const queue = await getSyncQueue();

    for (const entry of queue) {
      // Failed entries wait for manual review (retry/discard) — skip.
      if (isFailedEntry(entry)) {
        failed++;
        continue;
      }

      // Queued by another tenant or user, or unattributable: HOLD. The
      // queue replays through `apiClient`, which signs requests with
      // whatever token is current — sending this now would write into the
      // wrong tenant or under the wrong actor. Nothing is deleted; the
      // entry is surfaced for review and leaves on its own once its tenant
      // and its user are active again.
      if (isHeldEntry(entry, active)) {
        held++;
        continue;
      }

      // A 'never' row of BILLING_MUTATIONS is never replayed, whatever bundle
      // queued it: the entry is failed unsent and left for manual review.
      if (billingMutationRule(entry.method, entry.path)?.queue === 'never') {
        await markSyncQueueEntryError(entry.id, FISCAL_NOT_REPLAYED);
        failed++;
        continue;
      }

      if (entry.retryCount >= MAX_RETRIES) {
        await markSyncQueueEntryError(
          entry.id,
          'Nombre maximal de tentatives de synchronisation atteint'
        );
        failed++;
        continue;
      }

      // Not held, so attributed: `isHeldEntry` holds every entry without a
      // tenant.
      const entryTenantId = entry.tenantId as string;

      // The API refuses a dossier change made during the pass
      // (TENANT_MISMATCH) but has no such check for the user, so the token's
      // user is compared here. No `await` may separate this check from the
      // replay: `fetchApi` reads the token before its first await.
      if (getActiveUserId() !== entry.userId) {
        held++;
        continue;
      }

      try {
        // `replay` never enqueues: this loop alone decides retry/hold/fail.
        // `entry.id` is resent as the Idempotency-Key on every attempt, and
        // the entry's dossier as X-Tenant-ID, and the headers it was queued
        // with, when it has any.
        if (entry.headers !== undefined) {
          await apiClient.replay(entry.method, entry.path, entry.body, entry.id, entryTenantId, entry.headers);
        } else {
          await apiClient.replay(entry.method, entry.path, entry.body, entry.id, entryTenantId);
        }
        await removeSyncQueueEntry(entry.id);
        processed++;
      } catch (error: unknown) {
        const status = (error as { status?: number }).status;
        const code = (error as { code?: unknown }).code;
        if (code === TENANT_MISMATCH) {
          // Nothing was sent, or the API refused it unexecuted: the entry
          // waits, untouched, for its dossier to be active again.
          held++;
        } else if (code === IDEMPOTENCY_IN_PROGRESS || code === STOCK_CONCURRENT) {
          // The server is still processing an earlier attempt of this
          // entry, or a concurrent stock writer made it give way without
          // applying it: the entry is replayed unchanged on a later pass.
          await incrementRetry(entry.id);
          failed++;
        } else if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          // Any other client error (400/422 validation, 409 conflict,
          // 401/403, ...) is kept for manual review (retry/discard).
          const message =
            (error instanceof Error && error.message) ||
            'Le serveur a rejeté cette modification';
          await markSyncQueueEntryError(entry.id, message, status);
          failed++;
        } else {
          // Network/server error (5xx, 408, 429) — retry later
          await incrementRetry(entry.id);
          failed++;
        }
      }
    }

  } finally {
    isSyncing = false;
  }

  // Held entries mutate nothing, so the store emits no change event for
  // them — without this, a pass that holds work leaves the banner
  // counting it as « en attente ». Emit the fresh counts explicitly.
  notifyListeners(await getQueueCounts());

  return { processed, failed, held };
}

// ─── Manual review of failed entries ───

/** Entries the server rejected (state 'error'), oldest first. */
export async function listFailed(): Promise<SyncQueueEntry[]> {
  const queue = await getSyncQueue();
  return queue.filter(isFailedEntry);
}

// ─── Entries held back from the active session ───

/**
 * Entries that will NOT be replayed under the current session, oldest
 * first: queued by another tenant or user, or unattributable. Surfaced so
 * the operator can see what is being withheld and why — silently sitting
 * on a user's unsynced work is not acceptable either.
 */
export async function listHeld(): Promise<SyncQueueEntry[]> {
  const queue = await getSyncQueue();
  const active = getActiveSession();
  return queue.filter((entry) => isHeldEntry(entry, active));
}

/**
 * Catalogue key of the reason a held entry is being held.
 *
 * An entry with no tenant or no user has NO automatic future: no session
 * will ever claim it, so the operator must decide. An entry of another
 * user or tenant needs no decision — it leaves by itself once that user is
 * logged in on that tenant, which is why no « send it anyway » action is
 * offered: that button would send it under the wrong tenant or actor.
 * The user check comes before the tenant one: an entry of another user is
 * not sent on the next connection to its dossier by anyone else.
 */
export function heldReason(entry: SyncQueueEntry): TranslationKey {
  if (isUnattributableEntry(entry)) return 'offline.held.reason.unknownTenant';
  if (isUserUnattributableEntry(entry)) return 'offline.held.reason.unknownUser';
  if (entry.userId !== getActiveUserId()) return 'offline.held.reason.otherUser';
  return 'offline.held.reason.otherTenant';
}

/** Reset a failed entry and immediately attempt to sync it again. */
export async function retryEntry(id: string): Promise<void> {
  await resetSyncQueueEntry(id);
  // Desktop : l'API embarquée (loopback) reste joignable sans réseau —
  // navigator.onLine ne dit rien de sa disponibilité.
  if (isDesktopApp() || navigator.onLine) {
    await processSyncQueue();
  }
}

/** Permanently discard a failed entry (explicit user decision). */
export async function discardEntry(id: string): Promise<void> {
  await removeSyncQueueEntry(id);
}
